import React from "react";
import BehaviourBarGraph from "../BehaviourBarGraph.jsx";
import { formatNumber } from "@signolteam/shared/units-of-measure";
import AppUtils from "@src/utils/appUtils";

let defaultMetadata = {
  me_sfoc_corrected_g_kwh: 198, //units: g/kWh
  me_sfoc_expected_g_kwh: 203, //units: g/kWh
  esfoc_threshold_eng_1: 200, //units: g/kWh
  esfoc_threshold_eng_2: 205, //units: g/kWh
  me_1_sfoc_g_kwh: 198, //units: g/kWh
  me_2_sfoc_g_kwh: 203, //units: g/kWh
};

export const behaviourMetadataFields = Object.keys(defaultMetadata);
export default function graph({
  bvrColour,
  metadata = defaultMetadata,
  missingDataMsg,
  isExample,
  customerCode,
}) {
  const featureCheck = AppUtils.isFeatureAvailableForCustomer;
  let behaviourBarGraph = (
    <div className="relative text-center my-6 mx-auto text-2xl fill-current">
      {missingDataMsg}
    </div>
  );

  const enginesData = [];
  if (featureCheck("MultiEngineESFOC", customerCode)) {
    // find the number of engines from metadata me_*_sfoc_g_kwh
    const numEngines = Object.keys(metadata).filter((key) =>
      key.startsWith("esfoc_threshold_eng_"),
    ).length;
    if (numEngines === 0) {
      return behaviourBarGraph;
    }
    for (let i = 1; i <= numEngines; i++) {
      const actual = Number(metadata[`me_${i}_sfoc_g_kwh`]);
      const threshold = Number(metadata[`esfoc_threshold_eng_${i}`]);
      const ideal = threshold;
      const graphUnits = "g/kWh";

      const domainMin = 0;
      const margin = 0.2 * Number(actual);
      const domainMax =
        actual < ideal
          ? ideal + margin
          : Math.ceil(Number(actual) + Number(margin));
      const domain = [domainMin, domainMax];

      const mobileMargin =
        actual < threshold ? 0.1 * Number(threshold) : 0.1 * Number(actual);
      const mobileDomainMax =
        actual < ideal
          ? ideal + mobileMargin
          : Math.ceil(Number(actual) + Number(mobileMargin));
      const mobileDomain = [domainMin, mobileDomainMax];

      const thresholdLineColour = bvrColour.codes["900"];

      enginesData.push({
        name: `Actual ME ${i} SFOC`,
        mobileName: `Actual ME ${i}`,
        beforeThreshold: actual > threshold ? threshold : actual,
        afterThreshold: actual > threshold ? actual - threshold : 0,
        underThresholdColour:
          actual > threshold ? bvrColour.codes["200"] : bvrColour.codes["500"],
        overThresholdColour: bvrColour.codes["900"],
        domain: domain,
        mobileDomain: mobileDomain,
        unitOfMeasure: graphUnits,
        actual: actual,
        threshold: threshold,
        thresholdLineColour: thresholdLineColour,
      });
    }
  } else {
    const sfoc_value = Number(metadata["me_sfoc_corrected_g_kwh"]); //<100 and >300 is not acceptable - g/kWh
    const sfoc_threshold = Number(metadata["me_sfoc_expected_g_kwh"]);

    const actual = sfoc_value;
    const ideal = sfoc_threshold;
    const threshold = sfoc_threshold;
    const graphUnits = "g/kWh";
    const barName = "Actual SFOC";

    if (isNaN(sfoc_value) || isNaN(sfoc_threshold)) {
      return behaviourBarGraph;
    }

    const domainMin = 0;
    const margin = 0.2 * Number(actual);
    const domainMax =
      actual < ideal
        ? ideal + margin
        : Math.ceil(Number(actual) + Number(margin));
    const domain = [domainMin, domainMax];

    const mobileMargin =
      actual < threshold ? 0.1 * Number(threshold) : 0.1 * Number(actual);
    const mobileDomainMax =
      actual < ideal
        ? ideal + mobileMargin
        : Math.ceil(Number(actual) + Number(mobileMargin));
    const mobileDomain = [domainMin, mobileDomainMax];

    const thresholdLineColour = bvrColour.codes["900"];

    enginesData.push({
      name: barName,
      mobileName: "Actual",
      beforeThreshold: actual > threshold ? threshold : actual,
      afterThreshold: actual > threshold ? actual - threshold : 0,
      underThresholdColour:
        actual > threshold ? bvrColour.codes["200"] : bvrColour.codes["500"],
      overThresholdColour: bvrColour.codes["900"],
      domain: domain,
      mobileDomain: mobileDomain,
      unitOfMeasure: graphUnits,
      actual: actual,
      threshold: threshold,
      thresholdLineColour: thresholdLineColour,
    });
  }

  const graphs = (
    <>
      {enginesData.map((engineData, index) => {
        const data = [
          {
            name: engineData.name,
            mobileName: engineData.mobileName,
            beforeThreshold: engineData.beforeThreshold,
            afterThreshold: engineData.afterThreshold,
            underThresholdColour: engineData.underThresholdColour,
            overThresholdColour: engineData.overThresholdColour,
          },
        ];

        return (
          <BehaviourBarGraph
            key={index}
            data={data}
            threshold={engineData.threshold}
            thresholdLabel={`Ship-specific threshold${
              !isExample
                ? ` (${formatNumber(engineData.threshold, { unit: engineData.unitOfMeasure }).formattedValue})`
                : ""
            }`}
            mobileDomain={engineData.mobileDomain}
            domain={engineData.domain}
            refLineColour={engineData.thresholdLineColour}
            success={engineData.actual < engineData.threshold}
            unitOfMeasure={` ${engineData.unitOfMeasure}`}
            axisLabel={`Specific fuel-oil consumption (${engineData.unitOfMeasure})`}
            hideYLine={true}
            hideNum={true}
            barLabelFormatter={(value) =>
              `${formatNumber(value, { unit: engineData.unitOfMeasure }).formattedValue}`
            }
            isExample={isExample}
          />
        );
      })}
    </>
  );

  return graphs;
}
